<template>
  <v-line :config="hitLineConfig" />
  <v-rect ref="uiRect" :config="rectConfig" @contextmenu="onContextMenu" @wheel="handleWheel"
    @mousedown="onMouseDown" />
  <v-rect v-if="selectionRect.visible" :config="{
    x: Math.min(selectionRect.x1, selectionRect.x2),
    y: chartRenderStore.reverseScroll
      ? Math.min(stageHeight - selectionRect.y1, stageHeight - selectionRect.y2)
      : Math.min(selectionRect.y1, selectionRect.y2),
    width: Math.abs(selectionRect.x2 - selectionRect.x1),
    height: Math.abs(selectionRect.y2 - selectionRect.y1),
    fill: 'rgba(0,0,255,0.5)',
    listening: false,
  }" />
</template>

<script setup>
import { computed, inject, nextTick, onMounted, reactive, ref, defineEmits } from 'vue';
import { useAudioStore } from '../../stores/AudioStore';
import { useKeyboardEventStore } from '../../stores/KeyboardEventStore';
import { useSelectionStore } from '../../stores/SelectionStore';
import { useChartRenderStore } from '../../stores/ChartRenderStore';

const mainChartRender = inject('chartRender')
const { stageHeight, stageWidth, chartPositionX, chartPositionY, chartHeight, chartWidth, currentTime } = mainChartRender
const mainChart = inject('chart')
const { orderedCL, duration } = mainChart
const keyboardEventStore = useKeyboardEventStore()
const selectionStore = useSelectionStore()

const emit = defineEmits(['moveX', 'moveY'])

const chartRenderStore = useChartRenderStore()

const hitLineConfig = computed(() => {
  return {
    points: [0, chartRenderStore.hitPosY, stageWidth.value, chartRenderStore.hitPosY],
    stroke: '#f00',
    strokeWidth: 4
  }
});

const uiRect = ref(null)
const rectConfig = computed(() => {
  return {
    x: 0,
    y: 0,
    width: stageWidth.value,
    height: stageHeight.value,
  }
})

let canvas = null

onMounted(async () => {
  await nextTick()
  // idk if this is trustable
  canvas = uiRect.value.getNode().parent.parent.children[3].canvas._canvas
})

const onMouseDown = e => {
  e = e.evt
  if (canvas == null || canvas != e.target) return

  const x = mainChartRender.screenToChartPixelX(e.offsetX)
  const y = mainChartRender.screenToChartPixelY(e.offsetY)

  if (e.button == 0 && keyboardEventStore.clickMode == 0) {
    // regular mode / left click
    const cell = mainChartRender.getClosestVisibleCell(x, y)
    if (!cell) return;

    selectionStore.putSelectedNotesAtTargetCell(mainChart, cell)
  } else if (e.button == 2 && keyboardEventStore.clickMode == 0) {
    // regular mode / right click
    const note = mainChartRender.getClosestYNote(x, y)
    if (!note) return
    mainChart.removeNote(note.noteId)

  } else if (e.button == 0 && keyboardEventStore.clickMode == 1) {
    // select mode / left click
    selectHandleMouseDown(e)

  } else if (e.button == 2 && keyboardEventStore.clickMode == 1) {
    // select mode / right click
    selectionStore.setSelectedNotes(mainChart, [])

    isSelecting.value = false;
    selectEndPos.value = []
  }
}

const onMouseUp = e => {
  if (e.button == 0 && (keyboardEventStore.clickMode == 1 || (keyboardEventStore.clickMode == 0 && isSelecting.value))) {
    selectHandleMouseUp(e)
  }
}

const onClick = e => {
  if (canvas == null || canvas != e.target) return
  if (e.button == 0 && keyboardEventStore.clickMode == 1) {
    // selectHandleStageClick(e)
  }
}

const onMouseMove = e => {
  selectHandleMouseMove(e)
}

const onContextMenu = e => {
  e.evt.preventDefault()
}


// selection
const isSelecting = ref(false);

// physical x, y  chart pos x, y
let selectStartPos = ref([])
let selectEndPos = ref([])

const selectionRect = computed(() => {
  if (!isSelecting.value) {
    return { visible: false }
  }

  let x1, x2, y1, y2
  x1 = selectStartPos.value[0] - selectStartPos.value[2] + chartPositionX.value
  y1 = selectStartPos.value[1] + selectStartPos.value[3] - chartPositionY.value
  x2 = selectEndPos.value[0] != null ? selectEndPos.value[0] : x1
  y2 = selectEndPos.value[1] != null ? selectEndPos.value[1] : y1

  const res = {
    visible: true
    , x1, y1
    , x2, y2
  }
  return res
})

const selectHandleMouseDown = (e) => {
  const rect = canvas.getBoundingClientRect()
  // start selection rectangle
  isSelecting.value = true;
  selectStartPos.value = [e.pageX - rect.left - window.scrollX, e.pageY - rect.top - window.scrollY,
  chartPositionX.value, chartPositionY.value]
};

const selectHandleMouseMove = (e) => {
  if (!isSelecting.value) return
  const rect = canvas.getBoundingClientRect()
  selectEndPos.value = [e.pageX - rect.left - window.scrollX, e.pageY - rect.top - window.scrollY]
};

const selectHandleMouseUp = () => {
  if (!isSelecting.value) return;

  const lowPoint = {
    x: Math.min(selectionRect.value.x1, selectionRect.value.x2),
    y: chartRenderStore.reverseScroll
      ? Math.max(selectionRect.value.y1, selectionRect.value.y2)
      : Math.min(selectionRect.value.y1, selectionRect.value.y2),
  }
  const highPoint = {
    x: Math.max(selectionRect.value.x1, selectionRect.value.x2),
    y: chartRenderStore.reverseScroll
      ? Math.min(selectionRect.value.y1, selectionRect.value.y2)
      : Math.max(selectionRect.value.y1, selectionRect.value.y2),
  }

  const tooClose = highPoint.x - lowPoint.x < 15 && Math.abs(highPoint.y - lowPoint.y) < 15

  let notes
  if (tooClose) {
    notes = mainChartRender.getClosestYNote(
      mainChartRender.screenToChartPixelX(selectionRect.value.x2),
      mainChartRender.screenToChartPixelY(selectionRect.value.y2))

    notes = notes ? [[notes.noteId]] : []
  } else {
    notes = mainChartRender.getNotesWithinRange(
      mainChartRender.screenToChartPixelX(lowPoint.x),
      mainChartRender.screenToChartPixelY(lowPoint.y),
      mainChartRender.screenToChartPixelX(highPoint.x),
      mainChartRender.screenToChartPixelY(highPoint.y)
    )
  }

  selectionStore.setSelectedNotes(mainChart, notes)

  isSelecting.value = false;
  selectEndPos.value = []
};

// window.addEventListener("mousedown", onMouseDown)
window.addEventListener("mouseup", onMouseUp)
window.addEventListener("click", onClick)
window.addEventListener("mousemove", onMouseMove)

const handleWheel = (e) => {
  e.evt.preventDefault();
  let d = e.evt.deltaY;

  if (d == 0) return
  d = d < 0 ? -1 : 1

  if (e.evt.shiftKey) {
    const minX = 0;
    const maxX = chartWidth.value - stageWidth.value
    const x = Math.max(minX, Math.min(-chartPositionX.value + d * 200, maxX));

    mainChartRender.setPositionX(x / maxX)
  }
  else if (e.evt.ctrlKey) {
    chartRenderStore.setZoomY(chartRenderStore.zoomY + -d * 0.01)
  }
  else if (e.evt.altKey) {
    chartRenderStore.setSubdivision(chartRenderStore.subdivision - d)
  }
  else {
    if (keyboardEventStore.clickMode == 1) {
      const minY = 0
      const maxY = chartHeight.value
      const y = Math.max(minY, Math.min(-chartPositionY.value + (chartRenderStore.reverseScroll ? -d : d) * 200, maxY));
      emit('moveY', duration.value * y / chartHeight.value)
      return;
    }

    if (orderedCL.value.length <= 0) return

    const minY = 0
    const maxY = duration.value

    let clIndex = 0
    while (orderedCL.value[clIndex]?.endTime <= currentTime.value) clIndex++
    clIndex = Math.min(orderedCL.value.length - 1, clIndex)

    const beat = (currentTime.value - orderedCL.value[clIndex].eventLine.time) * orderedCL.value[clIndex].eventLine.bpm / 60
    const s = beat * chartRenderStore.subdivision
    let targetBeat = d * (chartRenderStore.reverseScroll ? 1 : -1) < 0
      ? Math.ceil(s + 0.001) / chartRenderStore.subdivision
      : Math.floor(s - 0.001) / chartRenderStore.subdivision

    targetBeat = Math.min(targetBeat, orderedCL.value[clIndex].durationInBeats)
    if (targetBeat < 0) {
      if (clIndex <= 0) {
        targetBeat = 0
      } else {
        clIndex--
        targetBeat = orderedCL.value[clIndex].durationInBeats - orderedCL.value[clIndex].durationInBeats % (1 / chartRenderStore.subdivision)

        if (targetBeat == orderedCL.value[clIndex].durationInBeats) targetBeat -= 1 / chartRenderStore.subdivision
      }
    }

    const y = Math.max(
      minY,
      Math.min(
        (60 / orderedCL.value[clIndex].eventLine.bpm) * targetBeat + orderedCL.value[clIndex].eventLine.time,
        maxY));

    emit('moveY', y)
  }
};
</script>
